import { useState } from 'react';
import { Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/sonner';
import { ConfirmDialog } from '@/components/Dialogs/ConfirmDialog';
import { apiErrorMessage } from '@/state/api-error';

export type RenameCopy = {
  /** The pencil's accessible name, and the dialog's title. */
  action: string;
  title: string;
  field: string;
  done: string;
  failed: string;
};

type RenameControlProps = {
  name: string;
  copy: RenameCopy;
  /** Saves the new name; throws when it did not go through. */
  onRename: (name: string) => Promise<void>;
};

/**
 * The pencil beside a details page's name and the dialog it opens: the experiment header's,
 * and the feature flag page's. The caller decides when it is offered.
 *
 * A failure is a toast and the dialog stays open with what was typed: apiErrorMessage, since
 * the API client throws a plain object, and a 400 or 404 reaches no global dialog.
 */
export const RenameControl = ({ name, copy, onRename }: RenameControlProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState(name);
  const trimmed = draft.trim();

  const open = () => {
    setDraft(name);
    setIsOpen(true);
  };

  const confirm = async () => {
    try {
      await onRename(trimmed);
      setIsOpen(false);
      toast.success(copy.done);
    } catch (error) {
      toast.error(apiErrorMessage(error, copy.failed));
    }
  };

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        aria-label={copy.action}
        title={copy.action}
        onClick={open}
        className="text-dim hover:text-foreground hover:bg-hover size-6 shrink-0 rounded-md"
      >
        <Pencil className="size-3.5" strokeWidth={1.5} />
      </Button>

      <ConfirmDialog
        title={copy.title}
        open={isOpen}
        onConfirm={confirm}
        onCancel={() => setIsOpen(false)}
        confirmLabel="Rename"
        disabled={!trimmed || trimmed === name}
      >
        <Label className="micro-label flex flex-col gap-2">
          {copy.field}
          <Input
            value={draft}
            maxLength={256}
            onChange={(ev) => setDraft(ev.target.value)}
            className="border-line bg-field text-foreground rounded-lg"
          />
          {!trimmed && <span className="supporting">Enter a name to continue.</span>}
        </Label>
      </ConfirmDialog>
    </>
  );
};
